/**
 * Landing Page Component
 * Public-facing landing page for Edsupp
 */
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useDarkMode from '../../hooks/useDarkMode';
import ThemeToggleButton from '../common/ThemeToggleButton';
import boyOne from '../../assets/child-avatars/boy-1.svg';
import boyTwo from '../../assets/child-avatars/boy-2.svg';
import girlOne from '../../assets/child-avatars/girl-1.svg';
import girlTwo from '../../assets/child-avatars/girl-2.svg';
import edsuppLogoBoy from '../../assets/logo/edsupp-logo-boy.svg';
import edsuppLogoGirl from '../../assets/logo/edsupp-logo-girl.svg';

const FILTER_PILLS = [
  { key: 'Parents',  emoji: '🏡',  label: 'For Parents',  active: 'bg-green-500 text-white shadow-green-200 shadow-md',  inactive: 'bg-white text-slate-600 border border-slate-200 hover:border-green-300 hover:text-green-700 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:border-green-500 dark:hover:text-green-400' },
  { key: 'Students', emoji: '🧒',  label: 'For Students', active: 'bg-orange-500 text-white shadow-orange-200 shadow-md', inactive: 'bg-white text-slate-600 border border-slate-200 hover:border-orange-300 hover:text-orange-700 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:border-orange-500 dark:hover:text-orange-400' },
  { key: 'Teachers', emoji: '👩‍🏫', label: 'For Teachers', active: 'bg-blue-500 text-white shadow-blue-200 shadow-md',   inactive: 'bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-700 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:border-blue-500 dark:hover:text-blue-400'  },
  { key: 'Schools',  emoji: '🏫',  label: 'For Schools',  active: 'bg-purple-400 text-slate-900 shadow-purple-200 shadow-md', inactive: 'bg-white text-slate-600 border border-slate-200 hover:border-purple-300 hover:text-purple-700 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:border-purple-500 dark:hover:text-purple-400' },
];

const FEATURES_BY_AUDIENCE = {
  Parents: [{
    icon: '🏡',
    title: 'Full Control with Parents',
    copy: 'Create worksheets, notes, videos and see your child\'s weekly progress at a glance.',
    accent: 'from-[#D9F99D] to-white',
  }, {
    icon: '✍️',
    title: 'Handwritten Work Evaluation',
    copy: 'Paper-based practice evaluated instantly — less screen time, full credit for real-world writing.',
    accent: 'from-[#DDD6FE] to-white',
  }, {
    icon: '📚',
    title: 'All Subjects',
    copy: 'Supports all subjects for complete learning. Get instant notifications when your child completes work.',
    accent: 'from-[#FDE68A] to-white',
  }, {
    icon: '🧘',
    title: 'Emotional Well-being',
    copy: 'Gentle daily mood check-ins surface early stress signals so you can step in before burnout hits.',
    accent: 'from-[#BAE6FD] to-white',
  }],
  Teachers: [{
    icon: '⚡',
    title: 'Instant Auto-Grading',
    copy: 'Every submission is checked the moment it\'s submitted — actionable feedback delivered without any wait.',
    accent: 'from-[#E0F2FE] to-white',
  }, {
    icon: '🎯',
    title: 'Concept Mastery Tracking',
    copy: 'Spot weak areas at a glance and auto-generate tailored worksheets to close every knowledge gap.',
    accent: 'from-[#FBCFE8] to-white',
  }, {
    icon: '📊',
    title: 'Shareable Progress Reports',
    copy: 'Generate clean, visual per-student or whole-class reports and share them with parents in seconds.',
    accent: 'from-[#BFDBFE] to-white',
  }, {
    icon: '📋',
    title: 'Assignment Management',
    copy: 'All worksheets, assignments, and class schedules organized in one place — no more lost papers.',
    accent: 'from-[#DCFCE7] to-white',
  }],
  Schools: [{
    icon: '🏫',
    title: 'School-Wide Dashboard',
    copy: 'Admins get class-level analytics, teacher performance snapshots, and district-ready reports in one view.',
    accent: 'from-[#FEF9C3] to-white',
  }, {
    icon: '📘',
    title: 'Curriculum Alignment',
    copy: 'Ensure every grade stays in sync with the national curriculum across all subjects and learning levels.',
    accent: 'from-[#DCFCE7] to-white',
  }, {
    icon: '👥',
    title: 'Bulk Account Management',
    copy: 'Onboard an entire school in minutes — bulk-create teacher and student accounts effortlessly.',
    accent: 'from-[#FED7AA] to-white',
  }, {
    icon: '📑',
    title: 'Reports',
    copy: 'Generate progress reports for administrators with a single click.',
    accent: 'from-[#DDD6FE] to-white',
  }],
  Students: [{
    icon: '🏆',
    title: 'Olympiad Preparation',
    copy: 'Goal-driven practice paths and timed challenges designed to help every child compete with confidence.',
    accent: 'from-[#FED7AA] to-white',
  }, {
    icon: '🧑‍🏫',
    title: 'Eddy - AI Tutor',
    copy: 'AI tutor to explain concepts and answer your queries.',
    accent: 'from-[#FBCFE8] to-white',
  }, {
    icon: '🎮',
    title: 'Learning Games & Badges',
    copy: 'Earn badges, build streaks, and climb leaderboards — practice that genuinely feels like play.',
    accent: 'from-[#FDE68A] to-white',
  }, {
    icon: '🎨',
    title: 'Creative & Critical Thinking',
    copy: 'Design challenges, reading games, and curiosity puzzles that go far beyond rote memorisation.',
    accent: 'from-blue-200 to-white',
  }],
};

const childAvatars = [girlOne, boyOne, girlTwo, boyTwo];
const heroLogos = [edsuppLogoBoy, edsuppLogoGirl];

const LandingPage = () => {
  const { user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [activeLogoIndex, setActiveLogoIndex] = useState(0);
  const [showDeferredContent, setShowDeferredContent] = useState(false);
  const [activeFilter, setActiveFilter] = useState('Parents');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fadeInUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
  };

  const floatingCard = {
    animate: {
      y: [0, -8, 0],
      transition: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
    },
  };

  useEffect(() => {
    let cancelled = false;
    const loadDeferredSections = () => {
      if (!cancelled) {
        setShowDeferredContent(true);
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(loadDeferredSections, { timeout: 1200 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timeout = setTimeout(loadDeferredSections, 100);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);
  
  // Redirect to dashboard if already logged in
  if (user) {
    return <Navigate to="/dashboard" />;
  }

  const activeLogo = heroLogos[activeLogoIndex];

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-br from-[#FDE68A] via-[#FBCFE8] to-[#A5B4FC] text-slate-900 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950 dark:text-slate-100">
      <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/60 to-white/30 dark:from-slate-950/70 dark:via-slate-950/60 dark:to-slate-950/20" aria-hidden />
      <div className="absolute -right-12 top-24 h-64 w-64 rounded-full bg-white/30 blur-3xl dark:bg-slate-700/20" aria-hidden />
      <div className="absolute -left-20 bottom-16 h-72 w-72 rounded-full bg-[#E0F2FE]/50 blur-3xl dark:bg-indigo-900/20" aria-hidden />

      {/* Header */}
      <header className="sticky top-0 z-10 border-b bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-4 py-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex min-w-0 flex-shrink-0 items-center gap-3"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <motion.img
              src={activeLogo}
              alt="Edsupp Logo"
              className="h-10 w-10 flex-shrink-0 rounded-xl bg-white/90 p-1 shadow-sm"
              animate={{ rotate: [0, -2, 2, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            />
            <div>
              <h1 className="hidden text-sm font-bold text-white sm:inline">Your Learning Companion</h1>
            </div>
          </motion.div>
          <motion.nav
            className="flex flex-shrink-0 items-center gap-2 text-sm font-medium text-white/90 sm:gap-4"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex-shrink-0">
              <ThemeToggleButton
                isDarkMode={isDarkMode}
                onToggle={toggleDarkMode}
                testId="landing-theme-toggle"
                className="bg-white/20 hover:bg-white/30 dark:bg-slate-800 dark:hover:bg-slate-700"
              />
            </motion.div>
            <motion.a
              href="#features"
              className="hidden rounded-lg px-1 py-2 hover:bg-white/80 hover:text-slate-900 sm:inline-block"
              variants={fadeInUp}
            >
              Features
            </motion.a>
            <motion.a
              href="#how-it-works"
              className="hidden rounded-lg px-1 py-2 hover:bg-white/80 hover:text-slate-900 sm:inline-block"
              variants={fadeInUp}
            >
              How?
            </motion.a>
            <motion.a
              href="#pricing"
              className="hidden rounded-lg px-1 py-2 hover:bg-white/80 hover:text-slate-900 sm:inline-block"
              variants={fadeInUp}
            >
              Pricing
            </motion.a>
            <motion.a
              href="/auth?mode=login"
              className="flex-shrink-0 rounded-full bg-slate-900 px-3 py-2 text-white shadow-md transition hover:translate-y-[-1px] hover:bg-slate-800"
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={fadeInUp}
            >
              Login
            </motion.a>
            <motion.button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className="flex-shrink-0 rounded-lg p-2 hover:bg-white/20 sm:hidden"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              variants={fadeInUp}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </motion.button>
          </motion.nav>
        </div>
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="overflow-hidden border-t border-white/10 bg-indigo-700/95 sm:hidden"
            >
              <nav className="flex flex-col px-4 py-2 text-sm font-medium text-white/90">
                <a
                  href="#features"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg px-2 py-3 hover:bg-white/10"
                >
                  Features
                </a>
                <a
                  href="#how-it-works"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg px-2 py-3 hover:bg-white/10"
                >
                  How?
                </a>
                <a
                  href="#pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-lg px-2 py-3 hover:bg-white/10"
                >
                  Pricing
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="relative">
        {/* Hero Section */}
        <motion.section
          className="px-4 pb-16 pt-14 sm:px-6 lg:px-8 md:pt-20"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          variants={staggerContainer}
        >
          <div className="mx-auto grid max-w-7xl items-start gap-12 md:grid-cols-2">
            {/* LEFT COLUMN */}
            <motion.div className="space-y-10" variants={staggerContainer}>
              <motion.div className="animate-learner-slide" variants={fadeInUp}>
                <div
                  className="flex min-h-[88px] flex-wrap items-center gap-4 rounded-2xl bg-white/60 px-4 py-3 shadow-sm ring-1 ring-white/80 transition hover:-translate-y-1 hover:shadow-lg dark:bg-slate-800/60 dark:ring-slate-700/60"
                >
                  <div className="flex -space-x-2">
                    {childAvatars.map((avatar, index) => (
                      <img
                        key={avatar}
                        src={avatar}
                        alt={`Smiling learner avatar ${index + 1}`}
                        className="h-10 w-10 rounded-full border-2 border-white bg-white shadow-sm dark:border-slate-800"
                      />
                    ))}
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-slate-900 dark:text-white">Loved by curious learners</p>
                    <p className="text-sm text-slate-600 dark:text-slate-300">Playful practice that keeps kids motivated.</p>
                  </div>
                </div>
              </motion.div>

              <motion.div className="space-y-7" variants={fadeInUp}>
                <div className="space-y-4">
                  <h2 className="text-3xl font-black leading-tight text-slate-900 dark:text-white sm:text-5xl">
                    Smart Support for Educators and Learners
                  </h2>
                  <p className="text-lg text-slate-600 dark:text-slate-300 sm:text-xl">
                    Edsupp keeps practice joyful, grading accurate, and insights clear so every child can thrive—with dashboards that feel calm for parents and playful for kids.
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <motion.a
                    href="/auth?mode=signup"
                    className="rounded-full bg-slate-900 px-3 py-2 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-white shadow-lg shadow-slate-900/10 transition hover:translate-y-[-1px] hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                    data-testid="get-started-btn"
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Get Started
                  </motion.a>
                  <motion.a
                    href="#features"
                    className="rounded-full border border-slate-900/10 bg-white/70 px-3 py-2 sm:px-6 sm:py-3 text-sm sm:text-base font-semibold text-slate-900 transition hover:border-slate-900/20 hover:bg-white dark:border-white/10 dark:bg-slate-800/70 dark:text-white dark:hover:border-white/20 dark:hover:bg-slate-800"
                    whileHover={{ y: -2, scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Explore
                  </motion.a>
                </div>
              </motion.div>
            </motion.div>

            {/* RIGHT COLUMN */}
            <motion.div className="relative space-y-6" variants={fadeInUp}>
              <div className="absolute -left-6 -top-6 h-16 w-16 rounded-full" aria-hidden />
                <motion.div className="flex flex-col items-center gap-4 px-6 py-8 text-center" variants={floatingCard} animate="animate">
                  <motion.div
                    className="w-56 h-56 sm:w-64 sm:h-64 rounded-2xl bg-white/90 p-4 shadow-md flex items-center justify-center dark:bg-slate-800/90"
                    whileHover={{ rotate: 1.5, scale: 1.02 }}
                  >
                    <motion.img
                      src={activeLogo}
                      alt="Edsupp Logo"
                      className="h-auto w-full max-w-[220px] rounded-2xl bg-white/90 p-4 object-contain shadow-md sm:max-w-[260px] md:max-w-[300px] dark:bg-slate-800/90"
                      animate={{ scale: [1, 1.03, 1] }}
                      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  </motion.div>
                  <p className="text-lg text-slate-700 dark:text-slate-300">
                    A trusted brand experience for parents, educators and young learners alike.
                  </p>
                </motion.div>
              </motion.div>
          </div>
        </motion.section>

        {showDeferredContent ? (
          <>
        {/* Features Section */}
        <motion.section
          id="features"
          className="relative overflow-hidden bg-white/80 px-4 py-16 shadow-inner sm:px-6 lg:px-8 dark:bg-slate-900/60"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <div className="mx-auto max-w-7xl">
            <motion.div className="text-center" variants={fadeInUp}>
              <p className="text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Features</p>
              <h3 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl">Everything parents & teachers need to feel confident</h3>
              <p className="mt-3 text-lg text-slate-600 dark:text-slate-300">
                From Olympiad readiness to emotional well-being, Edsupp delivers the support educators ask for.
              </p>
            </motion.div>

            {/* Interactive filter pills */}
            <div className="mt-8 flex flex-wrap justify-center gap-3" variants={fadeInUp}>
              {FILTER_PILLS.map((pill) => (
                <button
                  key={pill.key}
                  onClick={() => setActiveFilter(pill.key)}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                    activeFilter === pill.key ? pill.active : pill.inactive
                  }`}
                >
                  <span>{pill.emoji}</span>
                  {pill.label}
                </button>
              ))}
            </div>

            {/* Animated card grid — swaps on filter change */}
            <div className="mt-10 min-h-[220px]">
              <AnimatePresence mode="wait">
                <div
                  key={activeFilter}
                  className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut', staggerChildren: 0.08 } }}
                  exit={{ opacity: 0, y: -12, transition: { duration: 0.2 } }}
                >
                  {FEATURES_BY_AUDIENCE[activeFilter].map((feature, index) => (
                  <div
                    key={feature.title}
                    className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${feature.accent} p-6 shadow-sm ring-1 ring-white/80 transition duration-300 hover:-translate-y-2 hover:shadow-xl dark:from-slate-800 dark:to-slate-800/80 dark:ring-slate-700/60${
                      feature.highlight ? ' ring-2 ring-yellow-300/60' : ''
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.35, delay: index * 0.07 } }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/80 text-2xl shadow-sm transition duration-300 group-hover:scale-105 dark:bg-slate-700/80">
                        {feature.icon}
                      </span>

                      <span className="text-lg font-bold text-slate-900 dark:text-white">
                        {feature.title}
                      </span>
                    </div>

                    <p className="mt-4 text-sm text-slate-700 leading-relaxed dark:text-slate-300">
                      {feature.copy}
                    </p>
                  </div>
                ))}
                </div>
              </AnimatePresence>
            </div>
          </div>
        </motion.section>

        {/* How It Works Section */}
        <motion.section
          id="how-it-works"
          className="px-4 py-16 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <div className="mx-auto max-w-5xl">
            <motion.div className="text-center" variants={fadeInUp}>
              <p className="text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">How it works</p>
              <h3 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl">Smart support that fits into everyday learning</h3>
              <p className="mt-3 text-lg text-slate-600 dark:text-slate-300">
                Inspired by modern learning platforms, Edsupp pairs adaptive practice with rich insights so teachers, parents, and students stay aligned.
              </p>
            </motion.div>

            <motion.div className="mt-12 grid gap-6 md:grid-cols-3" variants={staggerContainer}>
              {[{
                title: 'Regular practice',
                copy: 'Learners receive tasks according to their conceptual level.',
                icon: '🎯',
              }, {
                title: 'Guided feedback',
                copy: 'See the "why" behind every score, with strengths and weaknesses identified.',
                icon: '🚀',
              }, {
                title: 'Personalisation',
                copy: 'Create personalised learning and growth goals to improve understanding.',
                icon: '📈',
              }].map((step, index) => (
                <div
                  key={step.title}
                  className="rounded-2xl bg-white/80 p-6 shadow-sm ring-1 ring-white/70 transition hover:-translate-y-1 hover:shadow-lg dark:bg-slate-800/80 dark:ring-slate-700/60"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#E0F2FE] to-[#FBCFE8] text-2xl dark:from-slate-700 dark:to-slate-700">
                      {step.icon}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500">Step {index + 1}</span>
                  </div>
                  <h4 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">{step.title}</h4>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{step.copy}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* Pricing Section */}
        <motion.section
          id="pricing"
          className="relative overflow-hidden bg-white/80 px-4 py-16 shadow-inner sm:px-6 lg:px-8 dark:bg-slate-900/60"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <div className="mx-auto max-w-7xl">
            <motion.div className="text-center" variants={fadeInUp}>
              <p className="text-sm font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-400">Pricing</p>
              <h3 className="mt-2 text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl">Flexible plans for parents and teachers</h3>
              <p className="mt-3 text-lg text-slate-600 dark:text-slate-300">
                Start for free, scale to classrooms, and partner with us district-wide when you&apos;re ready.
              </p>
            </motion.div>

            <motion.div className="mt-12 grid gap-6 lg:grid-cols-3" variants={staggerContainer}>
              {[{
                title: 'Basic',
                price: '₹99 per month',
                description: 'Perfect for playful practice.',
                perks: ['Single child profile', 'Basic features', 'Limited worksheets & evaluation', 'Learning games'],
                accent: 'from-[#E0F2FE] to-white',
                cta: 'Start Basic',
              }, {
                title: 'Pro',
                price: '₹499 per month',
                description: 'Perfect for regular learning.',
                perks: ['Unlimited workhseets & evaluation', 'Olympiad support', 'Coaching teacher integration', 'Personalised learning'],
                accent: 'from-[#FBCFE8] to-white',
                cta: 'Go Pro',
              }, {
                title: 'Premium',
                price: '₹999 per month',
                description: 'Perfect for insightful learning.',
                perks: ['All features of Pro', 'Performance analytics', 'Creative & design learning', 'Emotional wellbeing support'],
                accent: 'from-[#D9F99D] to-white',
                cta: 'Be Premium',
              }].map((plan) => (
                <div
                  key={plan.title}
                  className={`relative flex h-full flex-col rounded-3xl bg-gradient-to-br ${plan.accent} p-6 ring-1 ring-white/70 transition hover:-translate-y-1 dark:from-slate-800 dark:to-slate-800/80 dark:ring-slate-700/60 ${
                    plan.title === 'Pro'
                      ? 'shadow-2xl scale-105 ring-2 ring-indigo-300/50 z-10'
                      : 'shadow-lg hover:shadow-xl'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <h4 className="text-xl font-bold text-slate-900 dark:text-white">{plan.title}</h4>
                    {plan.title === 'Pro' && (
                      <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white">
                        Most popular
                      </span>
                    )}
                    {plan.title === 'Premium' && (
                      <span className="rounded-full bg-orange-700 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white">
                        All Access
                      </span>
                    )}
                  </div>
                  <p className="mt-4 text-3xl font-bold text-slate-900 dark:text-white">{plan.price}</p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">{plan.description}</p>
                  <ul className="mt-4 space-y-2 text-sm text-slate-700 dark:text-slate-300">
                    {plan.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2">
                        <span className="text-indigo-500 dark:text-indigo-400">•</span>
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/auth?mode=signup"
                    className={`mt-6 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold shadow-sm transition hover:-translate-y-0.5 ${
                      plan.title === 'Pro'
                        ? 'bg-slate-900 text-white hover:bg-slate-700'
                        : plan.title === 'Premium'
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:opacity-90'
                        : 'bg-sky-600 text-white hover:bg-sky-700'
                    }`}
                  >
                    {plan.cta}
                  </a>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.section>

          </>
        ) : (
          <section className="px-4 py-12 sm:px-6 lg:px-8" aria-live="polite" aria-busy="true">
            <div className="mx-auto max-w-7xl animate-pulse space-y-4">
              <div className="h-7 w-48 rounded-lg bg-white/70 dark:bg-slate-800/70" />
              <div className="h-4 w-full rounded-lg bg-white/60 dark:bg-slate-800/60" />
              <div className="grid gap-4 md:grid-cols-3">
                <div className="h-32 rounded-2xl bg-white/70 dark:bg-slate-800/70" />
                <div className="h-32 rounded-2xl bg-white/70 dark:bg-slate-800/70" />
                <div className="h-32 rounded-2xl bg-white/70 dark:bg-slate-800/70" />
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/60 bg-white/80 px-4 py-8 text-slate-700 shadow-inner sm:px-6 lg:px-8 dark:border-slate-800 dark:bg-slate-900/80 dark:text-slate-300">
        <motion.div
          className="mx-auto flex max-w-7xl flex-col items-center gap-2 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
          variants={fadeInUp}
        >
          <div className="flex items-center gap-3">
            <motion.img
              src={activeLogo}
              alt="Edsupp Logo"
              className="h-9 w-9 rounded-xl"
              animate={{ rotate: [0, 3, -3, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            />
            <h5 className="text-lg font-bold text-slate-900 dark:text-white">Edsupp</h5>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">Smart Support. Strong Education.</p>
          <p className="text-xs text-slate-500 dark:text-slate-500">© 2025 Oneed Innovations Private Limited. All rights reserved.</p>
        </motion.div>
      </footer>

      {/* Mobile sticky CTA — visible only on small screens */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-50 border-t border-slate-100 bg-white/90 backdrop-blur-md px-4 py-3 flex gap-3 dark:border-slate-800 dark:bg-slate-900/90">
        <a
          href="/auth?mode=signup"
          className="flex-1 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-violet-600 to-pink-500 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:opacity-90"
        >
          Get Started — Free
        </a>
        <a
          href="/auth?mode=login"
          className="rounded-full bg-slate-900 px-4 py-3 text-white text-sm font-semibold shadow-md transition hover:translate-y-[-1px] hover:bg-slate-800"
        >
          Login
        </a>
      </div>
    </div>
  );
};

export default LandingPage;
